const cents = value => Math.round(Number(value) * 100);

// La RPC entrega una fila por cuenta y mes, sin APE, REG, CIE ni CIV.
// Cada cuenta contribuye una sola vez, sin añadir las facturas del dashboard.
export function profitLoss(rows, names = {}) {
  const accounts = new Map();
  for (const row of rows) {
    const code = String(row.account || '').trim();
    if (!/^[67]/.test(code)) continue;
    const amount = Number(code[0] === '7' ? row.income : row.expense);
    if (!Number.isFinite(amount)) throw new Error('Hay importes contables no válidos.');
    const account = accounts.get(code) || { account: code, description: row.description || names[code] || 'Sin descripción', cents: 0 };
    account.cents += cents(amount);
    accounts.set(code, account);
  }
  const groups = new Map();
  for (const account of [...accounts.values()].sort((a, b) => a.account.localeCompare(b.account))) {
    const code = account.account.slice(0, 2);
    const group = groups.get(code) || { code, description: names[code] || `Grupo ${code}`, cents: 0, accounts: [] };
    group.cents += account.cents;
    group.accounts.push({ ...account, amount: account.cents / 100 });
    groups.set(code, group);
  }
  const total = prefix => [...accounts.values()].filter(a => a.account.startsWith(prefix)).reduce((sum, a) => sum + a.cents, 0);
  const income = total('7'), expense = total('6');
  return { income: income / 100, expense: expense / 100, result: (income - expense) / 100,
    groups: [...groups.values()].map(group => ({ ...group, amount: group.cents / 100 })) };
}

export const jointResultNote = 'Ambas empresas integradas por cuenta: contabilidad Ennage, ventas Genaen en 700 y bases de compras Genaen en sus cuentas de gasto. Los gastos comunes se registran en Ennage y no se añaden otra vez. Pendiente de conciliar existencias, gastos, operaciones internas y posibles duplicidades; no es un cierre consolidado definitivo.';
export function selectedProfitLoss(data, company = 'iva') {
  if (!['iva', 'a', 'all'].includes(company)) throw new Error('Empresa no válida');
  if (company !== 'a' && !Array.isArray(data.accounts)) throw new Error('Faltan cuentas');
  if (company !== 'iva' && !Array.isArray(data.invoices)) throw new Error('Faltan facturas');
  const accounting = profitLoss(company === 'a' ? [] : data.accounts, data.account_names);
  const invoices = company === 'iva' ? [] : data.invoices.filter(r => r.company === 'a');
  const total = kind => invoices.filter(r => r.kind === kind).reduce((sum, r) => {
    if (r.net == null || r.net === '' || !Number.isFinite(Number(r.net))) throw new Error('Factura con importe desconocido');
    return sum + cents(r.net);
  }, 0);
  const sales = total('sales'), purchases = total('purchases');
  const rows = company === 'a' ? [] : [...data.accounts];
  for (const invoice of invoices) {
    const month = invoice.date?.slice(0, 7);
    if (invoice.kind === 'sales') {
      rows.push({ month, account: '7000000', description: data.account_names?.['7000000'] || 'Ventas', income: Number(invoice.net), expense: 0 });
    } else if (invoice.kind === 'purchases') {
      if (!Array.isArray(invoice.expense_allocations)) throw new Error('Falta la distribución de gastos de Genaen. Aplica la actualización de cuentas de gasto en la base de datos.');
      let allocated = 0;
      for (const line of invoice.expense_allocations) {
        if (line.amount == null || !Number.isFinite(Number(line.amount))) throw new Error('Genaen contiene una base de gasto sin importe válido.');
        const amount = cents(line.amount);
        allocated += amount;
        if (!amount) continue;
        const account = String(line.account || '').trim();
        if (!/^6[0-9]+$/.test(account)) throw new Error(`Factura Genaen ${invoice.number || ''}: falta una cuenta válida del grupo 6 para una base con importe.`);
        rows.push({ month, account, description: data.account_names?.[account], income: 0, expense: amount / 100 });
      }
      if (allocated !== cents(invoice.net)) throw new Error(`Factura Genaen ${invoice.number || ''}: las bases por cuenta no coinciden con el importe neto.`);
    }
  }
  const integrated = profitLoss(rows, data.account_names);
  const { income, expense, result } = integrated;
  const title = company === 'all' ? 'Resultado conjunto estimado · Ennage y Genaen' : company === 'a' ? 'Cuenta de pérdidas y ganancias · Genaen' : 'Cuenta de pérdidas y ganancias · Ennage';
  const note = company === 'all' ? jointResultNote : company === 'a' ? 'Ventas Genaen en 700 y compras en sus cuentas del grupo 6, sin IVA. No incluye los gastos comunes registrados en Ennage ni constituye una cuenta de resultados completa.' : 'Grupos 6 y 7 de Ennage. Excluye APE, REG, CIE y CIV. No se añaden las facturas de Ennage al resultado contable.';
  const summary = [['Ingresos · grupo 7', income], ['Gastos · grupo 6', expense], [company === 'all' ? 'Resultado conjunto estimado' : 'Resultado del periodo', result]];
  return { ...integrated, company, accounting, rows, title, note, summary, genaenSales: sales/100, genaenPurchases: purchases/100 };

}
